import { useState } from "react";
import { FlaskConical, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  PREVIEW_FLAG_KEYS,
  isPreviewOverrideOn,
  setPreviewOverride,
  type PreviewFlagKey,
} from "@/config/environment";

const FLAG_META: Record<PreviewFlagKey, { title: string; desc: string }> = {
  competitorV2: {
    title: "Competitor — Top Pages",
    desc: "A Top Pages tab on Competitor Compare: your own domain auto-filled, competitor URLs, an Ahrefs traffic chart, and add-to-keyword-pipeline.",
  },
  pagegenDsh: {
    title: "Landing pages — DeepSeek lane",
    desc: "Builds the page on the PageGen DeepSeek engine. A run takes about thirteen minutes, so the page starts it and then polls history for the result rather than waiting on the request. ON FOR EVERYONE as of this release: this switch can force it on, not off, so leaving it off here changes nothing. To turn the lane off, set VITE_PAGEGEN_DSH=false.",
  },
};

export default function InternalPreviewPage() {
  const [state, setState] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(PREVIEW_FLAG_KEYS.map((k) => [k, isPreviewOverrideOn(k)])),
  );
  const [dirty, setDirty] = useState(false);

  const toggle = (key: PreviewFlagKey) => {
    const on = !state[key];
    setPreviewOverride(key, on);
    setState((s) => ({ ...s, [key]: on }));
    setDirty(true);
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center gap-3">
        <FlaskConical className="h-6 w-6 text-coral" />
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">Preview Features</h1>
          <p className="text-sm text-muted-foreground">
            Staff-only. Opt into in-progress features on this device — no deploy needed. Saved in your browser; reload to apply.
          </p>
        </div>
      </div>

      {dirty ? (
        <div className="flex items-center gap-3 rounded-lg border border-coral/40 bg-coral-light px-4 py-3 text-sm text-coral-dark">
          <RefreshCw className="h-4 w-4" />
          <span>Reload to apply your changes.</span>
          <Button size="sm" className="ml-auto h-8" onClick={() => window.location.reload()}>
            Reload now
          </Button>
        </div>
      ) : null}

      <Card className="border-0 shadow-sm">
        <ul className="divide-y divide-border">
          {PREVIEW_FLAG_KEYS.map((key) => (
            <li key={key} className="flex items-start gap-4 px-4 py-4">
              <div className="flex-1">
                <p className="font-medium text-foreground">{FLAG_META[key].title}</p>
                <p className="mt-0.5 text-sm text-muted-foreground">{FLAG_META[key].desc}</p>
              </div>
              <Switch checked={state[key]} onCheckedChange={() => toggle(key)} aria-label={FLAG_META[key].title} />
            </li>
          ))}
        </ul>
      </Card>

      <p className="text-xs text-muted-foreground">
        These override the build-time flags for your browser only. The environment's <code>VITE_*</code> vars still force a
        feature on everywhere when set. Clients never see these — the features stay staff-gated regardless.
      </p>
    </div>
  );
}
